/**
 * PATH: packages/ui/src/diagnostics/settingsTelemetry.ts
 * FILE_ID: UI-DIAG-SETTINGS-TELEMETRY-001
 * PURPOSE: Thin, namespaced wrapper over the existing KPI pipeline
 *          (clientPerfReporter.recordKpi) for Settings user actions, so every
 *          settings mutation lands in the 0x3admin perf store + PostHog without
 *          each call-site re-implementing telemetry.
 * WHY: The Settings surface had zero analytics — saves, integration connects,
 *      member invites, module toggles and theme changes were unrecorded. This
 *      reuses the existing recordKpi fan-out (no new infra) and gives a single
 *      verb-first API so coverage is consistent and call-sites stay tiny.
 *
 * FLOW:
 *   ┌──────────────┐   ┌───────────────────────┐   ┌──────────────────────┐
 *   │ settings UI   │ → │ track*  (this file)    │ → │ recordKpi → perf store │
 *   │ action        │   │ namespaces + status    │   │ + PostHog sink         │
 *   └──────────────┘   └───────────────────────┘   └──────────────────────┘
 *
 * DEPENDENCIES:
 *   - ./clientPerfReporter: recordKpi / markKpiStart / recordKpiSince
 * RELATED:
 *   - packages/settings-hub/src/feature/*: primary call-sites
 *   - packages/ui/src/components/settings/*: shared settings tab call-sites
 */
import { markKpiStart, recordKpi, recordKpiSince } from './clientPerfReporter'

const PREFIX = 'settings'

export type SettingsEventStatus = 'success' | 'error'

/**
 * trackSettingsEvent — record a discrete settings action with no meaningful
 * duration (e.g. a toggle, a connect click). Namespaced as
 * ``settings.<action>``. Best-effort: telemetry must never throw into the UI.
 */
export function trackSettingsEvent(
  action: string,
  extra?: Record<string, unknown>,
): void {
  if (!action) return
  try {
    recordKpi(`${PREFIX}.${action}`, 0, extra)
  } catch {
    // telemetry is best-effort — never break a settings interaction
  }
}

/**
 * trackSettingsSaveStart — mark the start of a save/flow so
 * ``trackSettingsSaveEnd`` can record its latency. Pair with exactly one
 * ``trackSettingsSaveEnd(action, ...)``.
 */
export function trackSettingsSaveStart(action: string): void {
  if (!action) return
  try {
    markKpiStart(`${PREFIX}.${action}`)
  } catch {
    // best-effort
  }
}

/**
 * trackSettingsSaveEnd — record the elapsed time since the matching
 * ``trackSettingsSaveStart(action)`` plus a success/error status. If no live
 * start mark exists (unpaired call), still emit a zero-duration event so the
 * action is never silently dropped.
 */
export function trackSettingsSaveEnd(
  action: string,
  status: SettingsEventStatus,
  extra?: Record<string, unknown>,
): void {
  if (!action) return
  const payload = { status, ...extra }
  try {
    const recorded = recordKpiSince(`${PREFIX}.${action}`, payload)
    if (recorded === null) {
      recordKpi(`${PREFIX}.${action}`, 0, payload)
    }
  } catch {
    // best-effort
  }
}

export const KANBAN_SETTINGS_ACTIONS = [
  'kanban.open',
  'kanban.page_change',
  'kanban.board_save',
  'kanban.lane_save',
  'kanban.face_save',
  'kanban.drawer_save',
  'kanban.pref_hydrate',
  'kanban.pref_sync',
] as const

export type KanbanSettingsAction = (typeof KANBAN_SETTINGS_ACTIONS)[number]

export interface KanbanSettingsTelemetryFields {
  controlId?: string
  scope?: string
  status?: string
  duration?: number
  actionId?: string
}

/** Exact extras backend `by_settings` projects for settings.kanban.*. */
export const KANBAN_SETTINGS_EXTRA_KEYS = [
  'controlId',
  'scope',
  'status',
  'actionId',
  'duration',
  'buildSha',
] as const

const CONTROL_ID = /^[A-Za-z0-9:._-]{1,80}$/
const ACTION_ID = /^[A-Za-z0-9_-]{8,64}$/
const SCOPE = /^(kanban:[A-Za-z0-9:._-]+|action|board|global|lane)$/
const BUILD_SHA = /^[a-fA-F0-9]{7,40}$/
const FORBIDDEN_TOKEN = /board[_-]?id|card[_-]?id|email|member|title|preference|payload|extra|user/i

function currentBuildSha(): string | null {
  let raw: string | undefined
  if (typeof document !== 'undefined') {
    raw = document.documentElement.dataset.buildSha
      || document.querySelector('[data-build-sha]')?.getAttribute('data-build-sha')
      || undefined
  }
  if (!raw && typeof window !== 'undefined') {
    raw = (window as { __QT_BUILD_SHA__?: string }).__QT_BUILD_SHA__
  }
  return safeToken(raw, BUILD_SHA)
}

function currentActionId(): string | null {
  if (typeof window === 'undefined') return null
  return safeToken(
    (window as { __QT_OBS__?: { currentActionId?: string | null } }).__QT_OBS__
      ?.currentActionId,
    ACTION_ID,
  )
}

function safeToken(value: unknown, pattern: RegExp): string | null {
  if (typeof value !== 'string') return null
  const token = value.trim()
  if (!token || token.includes('@') || FORBIDDEN_TOKEN.test(token)) return null
  return pattern.test(token) ? token : null
}

function normalizeStatus(value: unknown): 'success' | 'error' | 'unknown' {
  if (typeof value !== 'string') return 'unknown'
  const lowered = value.trim().toLowerCase()
  if (lowered === 'success' || lowered === 'ok') return 'success'
  if (lowered === 'error' || lowered === 'failed' || lowered === 'fail') return 'error'
  return 'unknown'
}

function normalizeDuration(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 600_000) {
    return 0
  }
  return Math.round(value * 100) / 100
}

/** Mint an allowlisted actionId. Backend drops tokens shorter than 8 chars. */
export function mintKanbanSettingsActionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    const hex = crypto.randomUUID().replace(/-/g, '')
    if (ACTION_ID.test(hex)) return hex
  }
  const minted = `kset${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
  return ACTION_ID.test(minted) ? minted : 'kset0001fallback'
}

/**
 * Shape extras to the backend by_settings allowlist.
 * Always emits controlId, scope, status, actionId, duration, buildSha.
 */
export function sanitizeKanbanSettingsExtras(
  fields: KanbanSettingsTelemetryFields = {},
): Record<(typeof KANBAN_SETTINGS_EXTRA_KEYS)[number], string | number | null> {
  return {
    controlId: safeToken(fields.controlId, CONTROL_ID),
    scope: safeToken(fields.scope, SCOPE),
    status: normalizeStatus(fields.status),
    actionId: safeToken(fields.actionId, ACTION_ID) || currentActionId() || mintKanbanSettingsActionId(),
    duration: normalizeDuration(fields.duration),
    buildSha: currentBuildSha(),
  }
}

/**
 * Record a Kanban settings event with only by_settings extras.
 * Never include board, card, title, email, member, or raw preference blobs.
 */
export function trackKanbanSettingsEvent(
  action: KanbanSettingsAction,
  fields: KanbanSettingsTelemetryFields = {},
): void {
  if (!KANBAN_SETTINGS_ACTIONS.includes(action)) return
  const extra = sanitizeKanbanSettingsExtras(fields)
  try {
    recordKpi(`${PREFIX}.${action}`, normalizeDuration(fields.duration), extra)
  } catch {
    // telemetry is best-effort
  }
}
